// Priority 2B-b — live dev-session activity feed over SSE.
//
// The backend tee's Claude's stream-json into a jsonl file and serves it at
// `GET /api/dev-sessions/:id/tail` as Server-Sent Events. Three named
// event types flow through: `tool`, `text`, `status`. A 15s `heartbeat`
// keeps proxies honest. EventSource's built-in retry + the Last-Event-ID
// header together recover from dropped connections without us threading
// any of that state by hand.
//
// This module owns the EventSource + a bounded in-memory model of the
// stream (current tool, last 5 files, rolling ~60-line text tail, latest
// metrics). The panel DOM is updated directly from event callbacks so we
// don't repaint on every tick.

import { API_BASE } from '../api';
import { post } from '../api';
import { state } from '../state';
import { showToast } from '../components/toast';
import {
  approveRejectButtonsHtml,
  bindApproveRejectActions,
  metricsLineHtml,
  type DevSession,
  type DevSessionMetric,
  type DevStatusColor,
  statusToColor,
} from './dev-sessions';

export interface ToolEvent {
  type: 'tool';
  tool_name: string;
  tool_input_preview: string;
  ts: string;
}

export interface TextEvent {
  type: 'text';
  text_delta: string;
  ts: string;
}

export interface StatusEvent {
  type: 'status';
  status?: string;
  metrics?: {
    tokens?: number;
    tokens_in?: number;
    tokens_out?: number;
    cost_usd?: number;
    context_pct?: number;
    files_touched?: number;
    commits_count?: number;
    model?: string;
    total_context_window?: number;
    phase?: string;
  };
  ts?: string;
  stream?: 'closed';
  resync?: boolean;
  dropped?: number;
}

// ── In-memory tail model ──────────────────────────────────────────────────
//
// Everything in the model is bounded — the backend ring buffer is 10 KB /
// 20 tool events, and the frontend matches those caps. If a session is
// hugely verbose, we still render in constant memory.

const MAX_TEXT_LINES = 60;
const MAX_FILES = 5;
const MAX_TOOLS = 10;

interface TailModel {
  sessionId: string;
  startedAtMs: number;
  currentTool: ToolEvent | null;
  files: string[];       // most-recent first
  textLines: string[];   // chronological; newest appended
  textBuffer: string;    // partial line accumulator (no trailing newline yet)
  latestStatus: StatusEvent | null;
  // v2 indicator fields
  tools: ToolEvent[];    // last 10, newest first
  phase: string;
  model: string;
  totalContextWindow: number;
  contextPct: number;
  tokensIn: number;
  tokensOut: number;
  costUsd: number;
}

function newTailModel(sessionId: string, startedAt: string): TailModel {
  return {
    sessionId,
    startedAtMs: Date.parse(startedAt) || Date.now(),
    currentTool: null,
    files: [],
    textLines: [],
    textBuffer: '',
    latestStatus: null,
    tools: [],
    phase: 'thinking',
    model: '',
    totalContextWindow: 200_000,
    contextPct: 0,
    tokensIn: 0,
    tokensOut: 0,
    costUsd: 0,
  };
}

// ── Live panel controller ─────────────────────────────────────────────────

export interface LiveTailHandle {
  close(): void;
}

export interface LiveTailOptions {
  session: DevSession;
  panelEl: HTMLElement;
  /** Called when we observe a status transition — e.g. to re-fetch the full
   *  DevSession from REST so approve/reject buttons stay in sync. */
  onStatusChange: () => void;
}

/**
 * Mount the live-tail panel into `panelEl`, subscribe to SSE, and return a
 * handle with a `close()` method. Idempotent in practice: calling mount
 * twice replaces the DOM + the EventSource. `panelEl` is the outer container
 * (caller provides).
 */
export function mountLiveTailPanel(opts: LiveTailOptions): LiveTailHandle {
  const { session, panelEl, onStatusChange } = opts;
  const model = newTailModel(session.id, session.started_at);
  const useV2 = state.flags['dev_thread_indicators_v2'] !== false;

  // Seed v2 fields from session metadata.
  model.model = session.agent_model || '';
  if (model.model.includes('1m') || model.model.includes('1M')) {
    model.totalContextWindow = 1_000_000;
  }

  // Initial seed from the session's latest_metric if present. This avoids
  // the panel looking blank for the first ~second before the first SSE
  // heartbeat/status arrives.
  if (session.latest_metric) {
    model.latestStatus = {
      type: 'status',
      status: session.status,
      metrics: metricToStatusMetrics(session.latest_metric),
    };
    const lm = session.latest_metric;
    model.contextPct = clampPct(lm.context_pct);
    model.tokensIn = Math.max(0, lm.tokens_in || 0);
    model.tokensOut = Math.max(0, lm.tokens_out || 0);
    model.costUsd = Math.max(0, lm.cost_usd_running || 0);
  }

  panelEl.classList.add('ds-live-panel');
  panelEl.innerHTML = useV2 ? panelHtmlV2(session) : panelHtml(session);
  refreshStaticParts(panelEl, session, model);

  // Elapsed clock — updates every 1s until terminal.
  const elapsedTimer = setInterval(() => {
    const el = panelEl.querySelector<HTMLElement>('.ds-live-elapsed');
    if (!el) return;
    el.textContent = formatElapsed(model.startedAtMs);
  }, 1000);

  // ── EventSource ───────────────────────────────────────────────────────
  const personId = state.ownerId || state.people[0]?.id || '';
  const url = `${API_BASE}/api/dev-sessions/${encodeURIComponent(session.id)}/tail?person_id=${encodeURIComponent(personId)}`;
  // EventSource sends credentials by default for same-origin URLs; for
  // proxied deployments where API_BASE prefixes the path we're still
  // same-origin so no extra flags needed.
  const es = new EventSource(url);

  // ── Debounced flush ─────────────────────────────────────────────────
  // Claude fires text_delta events at 10-30/sec during active generation.
  // Rendering on every event causes visible flashing from rapid DOM
  // reflows. Batch incoming text/tool events behind a short timer so we
  // repaint at most ~12 fps.
  const FLUSH_MS = 80;
  let flushTimer: number | null = null;
  let dirtyText = false;
  let dirtyTool = false;

  function scheduleFlush(): void {
    if (flushTimer != null) return;
    flushTimer = window.setTimeout(flushNow, FLUSH_MS);
  }

  function flushNow(): void {
    if (flushTimer != null) { clearTimeout(flushTimer); flushTimer = null; }
    if (dirtyTool) {
      renderToolBadge(panelEl, model);
      renderFilesList(panelEl, model);
      renderToolsList(panelEl, model);
      renderPhaseChip(panelEl, model);
      dirtyTool = false;
    }
    if (dirtyText) {
      renderRollingTail(panelEl, model);
      renderPhaseChip(panelEl, model);
      dirtyText = false;
    }
  }

  es.addEventListener('tool', (raw) => {
    const ev = tryParse<ToolEvent>((raw as MessageEvent).data);
    if (!ev || ev.type !== 'tool') return;
    model.currentTool = ev;
    model.tools = [ev, ...model.tools].slice(0, MAX_TOOLS);
    model.phase = 'tool_use';
    const fileHint = extractFileHint(ev.tool_input_preview);
    if (fileHint) {
      model.files = [fileHint, ...model.files.filter((f) => f !== fileHint)].slice(0, MAX_FILES);
    }
    dirtyTool = true;
    scheduleFlush();
  });

  es.addEventListener('text', (raw) => {
    const ev = tryParse<TextEvent>((raw as MessageEvent).data);
    if (!ev || ev.type !== 'text') return;
    model.phase = 'writing';
    appendText(model, ev.text_delta);
    dirtyText = true;
    scheduleFlush();
  });

  es.addEventListener('status', (raw) => {
    const ev = tryParse<StatusEvent>((raw as MessageEvent).data);
    if (!ev) return;
    // Server-initiated close — stop retrying, flush any pending content.
    if (ev.stream === 'closed') {
      flushNow();
      es.close();
      return;
    }
    // Status events are rare — render immediately and flush any pending
    // text/tool so the UI is fully up to date.
    flushNow();
    model.latestStatus = ev;
    // Update v2 model fields from enriched Status metrics.
    if (ev.metrics) {
      if (ev.metrics.model) model.model = ev.metrics.model;
      if (ev.metrics.total_context_window) model.totalContextWindow = ev.metrics.total_context_window;
      if (typeof ev.metrics.context_pct === 'number') model.contextPct = ev.metrics.context_pct;
      if (ev.metrics.phase) model.phase = ev.metrics.phase;
      if (typeof ev.metrics.tokens_in === 'number') model.tokensIn = ev.metrics.tokens_in;
      if (typeof ev.metrics.tokens_out === 'number') model.tokensOut = ev.metrics.tokens_out;
      if (typeof ev.metrics.cost_usd === 'number') model.costUsd = ev.metrics.cost_usd;
    }
    if (ev.status === 'done' || ev.status === 'error') model.phase = ev.status;
    renderStatusLine(panelEl, ev);
    renderMetrics(panelEl, ev);
    // v2 renderers (no-op on legacy DOM).
    renderModelBadge(panelEl, model);
    renderContextBar(panelEl, model);
    renderPhaseChip(panelEl, model);
    renderStatsLine(panelEl, model);
    // Coarse status transitions may affect approve/reject visibility —
    // tell the caller so it can refetch the canonical session.
    if (ev.status && ev.status !== session.status) {
      onStatusChange();
    }
  });

  // Heartbeat — no-op. Presence keeps the connection alive; nothing to paint.
  es.addEventListener('heartbeat', () => { /* ignore */ });

  es.addEventListener('error', () => {
    // EventSource auto-reconnects on transient errors. We just flag the
    // UI so the operator knows we're not live. Once events flow again, the
    // class is removed on the next tool/text/status handler run.
    panelEl.classList.add('ds-live-panel-reconnecting');
  });

  // ── Clean up ─────────────────────────────────────────────────────────
  return {
    close(): void {
      clearInterval(elapsedTimer);
      flushNow();
      es.close();
    },
  };
}

// ── DOM rendering ─────────────────────────────────────────────────────────

function panelHtml(session: DevSession): string {
  // Structural shell only; individual renderers fill in the dynamic bits.
  return `
    <div class="ds-live-head">
      <div class="ds-live-status-row">
        <span class="ds-dot ds-dot-${session.status_color}" data-role="status-dot" title="${esc(session.status)}"></span>
        <span class="ds-live-name">${esc(session.name)}</span>
        <span class="ds-live-elapsed" title="Elapsed since start">00:00</span>
      </div>
      <div class="ds-live-meta">
        <span class="ds-live-pill ds-live-worker">${esc(session.worker_container || session.agent_target)}</span>
        <span class="ds-live-pill ds-live-model">${esc(session.agent_model)}</span>
        <span class="ds-live-pill ds-live-mode">${esc(session.permission_mode)}</span>
      </div>
      <div class="ds-live-tool" data-role="tool-badge"></div>
      <div class="ds-live-metrics" data-role="metrics"></div>
    </div>
    <div class="ds-live-files">
      <div class="ds-live-section-label">Files touched</div>
      <ul class="ds-live-files-list" data-role="files"></ul>
    </div>
    <div class="ds-live-tail">
      <div class="ds-live-section-label">Output</div>
      <pre class="ds-live-tail-pre" data-role="tail" tabindex="0"></pre>
    </div>
    <div class="ds-live-actions" data-role="actions"></div>
  `;
}

function refreshStaticParts(panelEl: HTMLElement, session: DevSession, model: TailModel): void {
  const elapsed = panelEl.querySelector<HTMLElement>('.ds-live-elapsed');
  if (elapsed) elapsed.textContent = formatElapsed(model.startedAtMs);
  if (model.latestStatus) {
    renderMetrics(panelEl, model.latestStatus);
  } else if (session.latest_metric) {
    const m = panelEl.querySelector<HTMLElement>('[data-role="metrics"]');
    if (m) m.innerHTML = metricsLineHtml(session.latest_metric);
  }
  // v2 initial render (no-op on legacy DOM).
  renderModelBadge(panelEl, model);
  renderContextBar(panelEl, model);
  renderPhaseChip(panelEl, model);
  renderStatsLine(panelEl, model);
  const isAdmin = state.servers.some((sv) => sv.owner_id === state.ownerId);
  const actions = panelEl.querySelector<HTMLElement>('[data-role="actions"]');
  if (actions) {
    actions.innerHTML = cancelButtonHtml(session, isAdmin) + approveRejectButtonsHtml(session, isAdmin);
    bindCancelButton(actions, session);
    bindApproveRejectActions(actions, () => { /* caller handles refresh */ });
  }
}

// 2B-c — cancel button. Only visible when the session is still running /
// queued, and only to admins. The click reveals a 3s safety-timer countdown
// before the actual "Cancel now" action becomes clickable — protects against
// misclicks that would kill a valuable long-running session.
function cancelButtonHtml(session: DevSession, isAdmin: boolean): string {
  if (!isAdmin) return '';
  if (session.status !== 'running' && session.status !== 'queued') return '';
  return `<div class="ds-cancel-bar" data-role="cancel-bar">
    <button class="ds-cancel-btn" data-role="cancel-trigger" type="button">Cancel session…</button>
  </div>`;
}

function bindCancelButton(root: HTMLElement, session: DevSession): void {
  const trigger = root.querySelector<HTMLButtonElement>('[data-role="cancel-trigger"]');
  if (!trigger) return;
  trigger.addEventListener('click', () => {
    const bar = root.querySelector<HTMLElement>('[data-role="cancel-bar"]');
    if (!bar) return;
    let remaining = 3;
    bar.innerHTML = `
      <span class="ds-cancel-warn">Cancel this session?</span>
      <button class="ds-cancel-confirm" data-role="cancel-confirm" type="button" disabled>Cancel now (${remaining}s)</button>
      <button class="ds-cancel-keep" data-role="cancel-keep" type="button">Keep running</button>`;
    const confirm = bar.querySelector<HTMLButtonElement>('[data-role="cancel-confirm"]');
    const keep = bar.querySelector<HTMLButtonElement>('[data-role="cancel-keep"]');
    const timer = setInterval(() => {
      remaining -= 1;
      if (remaining <= 0) {
        clearInterval(timer);
        if (confirm) { confirm.disabled = false; confirm.textContent = 'Cancel now'; }
      } else if (confirm) {
        confirm.textContent = `Cancel now (${remaining}s)`;
      }
    }, 1000);
    keep?.addEventListener('click', () => {
      clearInterval(timer);
      bar.innerHTML = `<button class="ds-cancel-btn" data-role="cancel-trigger" type="button">Cancel session…</button>`;
      bindCancelButton(root, session);
    });
    confirm?.addEventListener('click', async () => {
      if (confirm.disabled) return;
      confirm.disabled = true;
      try {
        await post(`/api/dev-sessions/${encodeURIComponent(session.id)}/cancel`, {
          person_id: state.ownerId ?? '',
        });
        showToast('Cancelled', 'ok');
        clearInterval(timer);
      } catch (e) {
        showToast(`Cancel failed: ${String(e)}`, 'err');
        confirm.disabled = false;
      }
    });
  });
}

function renderToolBadge(panelEl: HTMLElement, model: TailModel): void {
  const el = panelEl.querySelector<HTMLElement>('[data-role="tool-badge"]');
  if (!el) return;
  const t = model.currentTool;
  if (!t) { el.textContent = ''; return; }
  el.innerHTML = `<span class="ds-live-tool-name">${esc(t.tool_name)}</span>
    <span class="ds-live-tool-input">${esc(truncate(t.tool_input_preview, 120))}</span>`;
  panelEl.classList.remove('ds-live-panel-reconnecting');
}

function renderFilesList(panelEl: HTMLElement, model: TailModel): void {
  const el = panelEl.querySelector<HTMLElement>('[data-role="files"]');
  if (!el) return;
  el.innerHTML = model.files
    .map((f) => `<li class="ds-live-file" title="Click to copy path">${esc(f)}</li>`)
    .join('');
  el.querySelectorAll<HTMLLIElement>('.ds-live-file').forEach((li) => {
    li.addEventListener('click', () => {
      const path = li.textContent || '';
      if (path) void navigator.clipboard.writeText(path);
    });
  });
}

function renderRollingTail(panelEl: HTMLElement, model: TailModel): void {
  const el = panelEl.querySelector<HTMLPreElement>('[data-role="tail"]');
  if (!el) return;
  // Auto-scroll heuristic: if the operator is within 20px of the bottom,
  // keep following; otherwise honour their scroll position.
  const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 20;
  // Concatenate with a trailing partial if present.
  const body = [...model.textLines, model.textBuffer].filter(Boolean).join('\n');
  el.textContent = body;
  if (nearBottom) el.scrollTop = el.scrollHeight;
  panelEl.classList.remove('ds-live-panel-reconnecting');
}

function renderStatusLine(panelEl: HTMLElement, ev: StatusEvent): void {
  const dot = panelEl.querySelector<HTMLElement>('[data-role="status-dot"]');
  if (dot && ev.status) {
    const color: DevStatusColor = statusToColor(ev.status);
    dot.className = `ds-dot ds-dot-${color}`;
    dot.title = ev.status;
  }
  panelEl.classList.remove('ds-live-panel-reconnecting');
  if (ev.stream === 'closed') {
    panelEl.classList.add('ds-live-panel-closed');
  }
  // resync hints carry no paint work — the ring-buffer replay that
  // accompanies them handles the state refresh.
}

function renderMetrics(panelEl: HTMLElement, ev: StatusEvent): void {
  const el = panelEl.querySelector<HTMLElement>('[data-role="metrics"]');
  if (!el) return;
  const m = ev.metrics;
  if (!m) { el.textContent = ''; return; }
  // Reuse the P2 metrics line shape so visual language stays consistent
  // with the dashboard Claude-usage card.
  const synthesized: DevSessionMetric = {
    id: '',
    session_id: '',
    polled_at: ev.ts || '',
    context_used_tokens: 0,
    context_max_tokens: 200_000,
    context_pct: clampPct(m.context_pct),
    tokens_in: Math.max(0, m.tokens_in ?? m.tokens ?? 0),
    tokens_out: Math.max(0, m.tokens_out ?? 0),
    cost_usd_running: Math.max(0, m.cost_usd ?? 0),
    files_touched: Math.max(0, m.files_touched ?? 0),
    commits_count: Math.max(0, m.commits_count ?? 0),
  };
  el.innerHTML = metricsLineHtml(synthesized);
}

// ── v2 indicator panel ───────────────────────────────────────────────────

function panelHtmlV2(session: DevSession): string {
  return `
    <div class="ds-tail-header" data-role="header">
      <span class="ds-tail-model-badge" data-role="model-badge">${esc(session.agent_model)}</span>
      <span class="ds-live-name">${esc(session.name)}</span>
      <div class="ds-tail-ctx-wrap">
        <span class="ds-tail-ctx-label" data-role="ctx-label">Context: 0%</span>
        <div class="ds-tail-ctx-bar ds-ctx-green" data-role="ctx-bar">
          <div class="ds-tail-ctx-fill" data-role="ctx-fill" style="width:0%"></div>
        </div>
      </div>
      <span class="ds-live-elapsed" title="Elapsed since start">00:00</span>
    </div>
    <div class="ds-tail-stats" data-role="stats">
      <span class="ds-tail-phase ds-phase-thinking" data-role="phase-chip">Thinking...</span>
      <span class="ds-tail-cost" data-role="cost-label">Cost: $0.00</span>
      <span class="ds-tail-tokens" data-role="tokens-label">Tokens: 0</span>
    </div>
    <div class="ds-tail-tools-section">
      <div class="ds-live-section-label">Tools (recent 10)</div>
      <ul class="ds-tail-tools-list" data-role="tools-list"></ul>
    </div>
    <div class="ds-live-tail">
      <div class="ds-live-section-label">Output</div>
      <pre class="ds-live-tail-pre" data-role="tail" tabindex="0"></pre>
    </div>
    <div class="ds-live-actions" data-role="actions"></div>
  `;
}

function renderModelBadge(panelEl: HTMLElement, m: TailModel): void {
  const el = panelEl.querySelector<HTMLElement>('[data-role="model-badge"]');
  if (!el || !m.model) return;
  el.textContent = m.model;
}

function renderContextBar(panelEl: HTMLElement, m: TailModel): void {
  const fill = panelEl.querySelector<HTMLElement>('[data-role="ctx-fill"]');
  const label = panelEl.querySelector<HTMLElement>('[data-role="ctx-label"]');
  const bar = panelEl.querySelector<HTMLElement>('[data-role="ctx-bar"]');
  if (!fill || !label || !bar) return;
  const pct = m.contextPct;
  fill.style.width = `${pct}%`;
  bar.classList.remove('ds-ctx-green', 'ds-ctx-amber', 'ds-ctx-red');
  if (pct >= 90) bar.classList.add('ds-ctx-red');
  else if (pct >= 70) bar.classList.add('ds-ctx-amber');
  else bar.classList.add('ds-ctx-green');
  const total = m.totalContextWindow >= 1_000_000 ? '1M' : `${Math.round(m.totalContextWindow / 1000)}k`;
  const used = Math.round(pct / 100 * m.totalContextWindow / 1000);
  label.textContent = `Context: ${Math.round(pct)}% (${used}k / ${total})`;
}

function renderPhaseChip(panelEl: HTMLElement, m: TailModel): void {
  const el = panelEl.querySelector<HTMLElement>('[data-role="phase-chip"]');
  if (!el) return;
  const labels: Record<string, string> = {
    thinking: 'Thinking...',
    writing: 'Writing...',
    idle: 'Idle (waiting)',
    done: 'Done',
    error: 'Error',
  };
  const label = m.phase === 'tool_use'
    ? `Using ${m.currentTool?.tool_name || 'tool'}...`
    : (labels[m.phase] || 'Thinking...');
  const cls = m.phase === 'tool_use' ? 'tool' : m.phase;
  el.className = `ds-tail-phase ds-phase-${cls}`;
  el.textContent = label;
}

function renderToolsList(panelEl: HTMLElement, m: TailModel): void {
  const el = panelEl.querySelector<HTMLElement>('[data-role="tools-list"]');
  if (!el) return;
  el.innerHTML = m.tools
    .map((t) => `<li class="ds-tail-tool-row"><span class="ds-tail-tool-name">${esc(t.tool_name)}</span><span class="ds-tail-tool-preview">${esc(truncate(t.tool_input_preview, 80))}</span></li>`)
    .join('');
}

function renderStatsLine(panelEl: HTMLElement, m: TailModel): void {
  const cost = panelEl.querySelector<HTMLElement>('[data-role="cost-label"]');
  const tokens = panelEl.querySelector<HTMLElement>('[data-role="tokens-label"]');
  if (cost) cost.textContent = `Cost: $${m.costUsd.toFixed(2)}`;
  if (tokens) {
    const fmt = (n: number) => n >= 1000 ? `${Math.round(n / 1000)}k` : String(n);
    tokens.textContent = `Tokens: ${fmt(m.tokensIn)} \u2193 ${fmt(m.tokensOut)}`;
  }
}

// ── helpers ───────────────────────────────────────────────────────────────

function appendText(model: TailModel, delta: string): void {
  // Merge into the accumulator, then split completed lines out.
  model.textBuffer += delta;
  let nl = model.textBuffer.indexOf('\n');
  while (nl !== -1) {
    const line = model.textBuffer.slice(0, nl);
    model.textLines.push(line);
    model.textBuffer = model.textBuffer.slice(nl + 1);
    nl = model.textBuffer.indexOf('\n');
  }
  if (model.textLines.length > MAX_TEXT_LINES) {
    model.textLines = model.textLines.slice(model.textLines.length - MAX_TEXT_LINES);
  }
}

function extractFileHint(preview: string): string | null {
  // Best-effort: pull the first path-like token out of the preview. Tool
  // previews from the bridge look like `path=src/foo.rs` or `src/foo.rs`
  // — either way the first slash-containing token wins.
  if (!preview) return null;
  const m = preview.match(/[a-zA-Z0-9_./-]+\/[a-zA-Z0-9_.-]+/);
  return m?.[0] ?? null;
}

function metricToStatusMetrics(m: DevSessionMetric): StatusEvent['metrics'] {
  return {
    tokens_in: m.tokens_in,
    tokens_out: m.tokens_out,
    cost_usd: m.cost_usd_running,
    context_pct: m.context_pct,
    files_touched: m.files_touched,
    commits_count: m.commits_count,
  };
}

function formatElapsed(startedAtMs: number): string {
  const secs = Math.max(0, Math.floor((Date.now() - startedAtMs) / 1000));
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const s = secs % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${pad(m)}:${pad(s)}`;
}

function esc(s: string): string {
  const el = document.createElement('div');
  el.textContent = s;
  return el.innerHTML;
}

function truncate(s: string, max: number): string {
  if (s.length <= max) return s;
  return s.slice(0, max - 1) + '…';
}

function clampPct(n: number | undefined): number {
  if (typeof n !== 'number' || !Number.isFinite(n)) return 0;
  return Math.min(100, Math.max(0, n));
}

function tryParse<T>(s: string): T | null {
  try { return JSON.parse(s) as T; } catch { return null; }
}
